/**
 * @fileoverview Product hero: image (links to product management), name, and
 * the unified metrics panel. Replaces the old KPI tab-strip + mini chart.
 * @module features/home/components/product-statistics/ProductHero
 */

import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { formatInt } from '@/shared/utils/metricsUtils'
import type { StatisticsMetricsDTO } from '@/lib/statisticsService'
import type { PreviousPeriodTotals } from '@/features/home/hooks/useProductStatistics'
import { ProductMetricsPanel } from './ProductMetricsPanel'

interface ProductHeroProps {
  productId?: string
  productName: string
  imageUrl: string | null
  metrics: StatisticsMetricsDTO
  previous?: PreviousPeriodTotals | undefined
  className?: string
}

export function ProductHero({
  productId,
  productName,
  imageUrl,
  metrics,
  previous,
  className,
}: ProductHeroProps): ReactElement {
  const { t } = useTranslation('features-home')
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-[168px_1fr] gap-4', className)}>
      <Link
        to={productId ? `/dashboard/management/products/${productId}` : '#'}
        className="glass-card !p-0 overflow-hidden cursor-pointer self-start"
      >
        <div className="aspect-square w-full overflow-hidden bg-muted/20">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={productName}
              className="w-full h-full object-cover brightness-[0.7] hover:brightness-[0.85] transition-[filter]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground/30 text-4xl font-semibold">
              {productName.trim().replace(/^\W+/, '').slice(0, 2).toUpperCase()}
            </div>
          )}
        </div>
      </Link>

      <div className="min-w-0 flex flex-col gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="text-3xl md:text-4xl font-semibold text-foreground truncate">{productName}</h1>
          <span className="shrink-0 rounded-full border border-border/40 bg-muted/30 px-2.5 py-1 text-xs font-medium text-muted-foreground whitespace-nowrap">
            {t('productStatistics.kpi.soldCount', { value: formatInt(metrics.quantity ?? 0) })}
          </span>
          {(metrics.cancelledQuantity ?? 0) > 0 && (
            <span className="shrink-0 rounded-full border border-negative/40 bg-muted/30 px-2.5 py-1 text-xs font-medium text-muted-foreground whitespace-nowrap">
              {t('productStatistics.kpi.refundedCount', { value: formatInt(metrics.cancelledQuantity ?? 0) })}
            </span>
          )}
        </div>
        <ProductMetricsPanel metrics={metrics} previous={previous} className="flex-1" />
      </div>
    </div>
  )
}
